import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { InlineTip } from '../components/InlineTip';
import { Target, MapPin, DollarSign, ClipboardCheck, Pencil, ExternalLink, Phone } from 'lucide-react';
import { HelpTooltip } from '../components/HelpTooltip';
import api from '../utils/api';

const GOALS = [
  { id: 'get_calls_now', label: 'Get calls now', desc: 'Emergency & repair work this week' },
  { id: 'book_more_jobs', label: 'Book more jobs', desc: 'Steady flow of booked appointments' },
  { id: 'fill_slow_schedule', label: 'Fill a slow schedule', desc: 'Keep trucks busy in the slow weeks' },
  { id: 'sell_higher_ticket', label: 'Sell higher-ticket jobs', desc: 'Replacements, installs, upgrades' },
];

const TIERS = [
  { id: 'test', label: 'Test the waters', daily: 40, leads: '~4–6 leads/wk' },
  { id: 'grow', label: 'Grow steadily', daily: 90, leads: '~10–16 leads/wk', recommended: true },
  { id: 'aggressive', label: 'Go aggressive', daily: 175, leads: '~20–30 leads/wk' },
];

const STEPS = ['Goal', 'Area', 'Budget', 'Review', 'Launch'];

const TRADE_AD_IMAGES = {
  HVAC: 'https://static.prod-images.emergentagent.com/jobs/6c107657-a40e-4170-a322-d7aa39016c78/images/2991f2ee65e85f771c11ca144510d14fa63ed3ab5c3f74c89cb673d2c3b4bd04.jpeg',
  Plumbing: 'https://static.prod-images.emergentagent.com/jobs/6c107657-a40e-4170-a322-d7aa39016c78/images/cd00871080d3dfe55c07fc997f864679950f9950b55980a7b3effb0d750493bc.jpeg',
  Electrical: 'https://static.prod-images.emergentagent.com/jobs/6c107657-a40e-4170-a322-d7aa39016c78/images/20f8e940d37fc1de12c3f192278fc7e3d53a2f9082941db94ac78df9734e6da0.jpeg',
};

const TIMELINE = [
  ['Right now', 'Your campaign enters review. Google/Meta usually approve within 24 hours.'],
  ['Days 1–2', "The system is learning who clicks and who calls. Numbers look rough — that's normal."],
  ['Days 3–5', 'Costs settle and the first real leads come in. Answer every call fast.'],
  ['Day 7', "First real read on performance. Don't turn it off early — stopping resets the learning."],
];

const METRICS = [
  ['CPC — Cost Per Click', 'What you pay each time someone clicks your ad. Good in the trades: $5–$15 for most services, up to $30+ for emergency HVAC.'],
  ['CTR — Click-Through Rate', 'Of the people who SEE your ad, how many click it. Good: 4%+ on search ads. Low CTR usually means the ad copy doesn\u2019t match what they searched.'],
  ['CPL — Cost Per Lead', 'What you pay for each real lead (a call or form). Good: $30–$80 plumbing/electrical, $50–$150 HVAC. This is the number to watch.'],
  ['ROI — Return On Investment', 'Money made vs. money spent. If $500 of ads books $3,000 of jobs, that\u2019s 6x. Anything above 3x in the trades is working.'],
];

export default function CampaignLaunchWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [goal, setGoal] = useState(null);
  const [areaType, setAreaType] = useState('radius');
  const [areaValue, setAreaValue] = useState('15 miles');
  const [tier, setTier] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [done, setDone] = useState(null);

  // Creative preview state
  const [campaign, setCampaign] = useState(null);
  const [adVariants, setAdVariants] = useState([]);
  const [adIndex, setAdIndex] = useState(0);
  const [copyOverrides, setCopyOverrides] = useState({});
  const [editingAd, setEditingAd] = useState(false);
  const [draftCopy, setDraftCopy] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [customImg, setCustomImg] = useState('');
  const [company, setCompany] = useState(null);
  const [adConnected, setAdConnected] = useState(null);

  useEffect(() => {
    api.get('/guided/connections/status').then(({ data }) => {
      setAdConnected(!!(data.google?.connected || data.meta?.connected));
    }).catch(() => setAdConnected(false));
  }, []);

  useEffect(() => {
    api.get(`/campaigns/${id}`).then(({ data }) => {
      setCampaign(data);
      setImageUrl(TRADE_AD_IMAGES[data.trade] || TRADE_AD_IMAGES.HVAC);
    }).catch(() => {});
    api.get(`/campaigns/${id}/assets`).then(({ data }) => {
      const ads = (data || []).filter((a) => /^Facebook Ad \d/.test(a.asset_type || '') && a.content);
      setAdVariants(ads);
    }).catch(() => {});
    api.get('/profile/business').then(({ data }) => {
      setCompany(data);
      if (data?.logo_url) setLogoUrl(data.logo_url);
    }).catch(() => {});
  }, [id]);

  const currentAd = adVariants[adIndex];
  const adContent = currentAd ? (copyOverrides[currentAd.id] ?? currentAd.content) : '';
  const adLines = adContent.split('\n').filter((l) => l.trim());
  const adHeadline = adLines[0] || '';
  const adBody = adLines.slice(1).join('\n');

  const launch = async () => {
    setLaunching(true);
    try {
      const { data } = await api.post(`/wizard/campaigns/${id}/launch`, {
        goal: goal.id, area_type: areaType, area_value: areaValue, budget_tier: tier.id,
        ad_asset_id: currentAd?.id || null, ad_copy: adContent || null, ad_image_url: imageUrl || null,
      });
      setDone(data.plan);
      setStep(4);
    } catch {
      setLaunching(false);
    }
  };

  if (done) {
    return (
      <Layout variant="light">
        <div className="max-w-2xl mx-auto text-center" data-testid="launch-success">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 mt-4">You launched your {done.campaign_name} campaign</h1>
          <p className="text-light-gray mb-10">${done.daily_budget}/day · {done.area_value} · running in your account.</p>

          <div className="text-left rounded-2xl border border-divider bg-card-bg p-6 mb-6" data-testid="launch-timeline">
            <h2 className="text-xl font-bold text-white mb-4">What happens next</h2>
            <div className="space-y-4">
              {TIMELINE.map(([t, d]) => (
                <div key={t} className="flex gap-4">
                  <span className="shrink-0 w-24 text-lime font-bold text-sm">{t}</span>
                  <p className="text-sm text-light-gray">{d}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-left rounded-2xl border border-divider bg-card-bg p-6 mb-8" data-testid="numbers-decoded">
            <h2 className="text-xl font-bold text-white mb-4">Your numbers, decoded</h2>
            <div className="space-y-4">
              {METRICS.map(([term, def]) => (
                <div key={term}>
                  <p className="text-white font-bold text-sm">{term}</p>
                  <p className="text-sm text-light-gray">{def}</p>
                </div>
              ))}
            </div>
          </div>

          <Link to={`/campaign/${id}`} className="btn-primary inline-block px-8 py-3" data-testid="back-to-campaign-btn">Open your campaign kit →</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout variant="light">
      <div className={`mx-auto ${step === 3 ? 'max-w-3xl' : 'max-w-lg'}`} data-testid="launch-wizard">
        <div className="mb-2 flex items-center justify-between">
          <button onClick={() => navigate(`/campaign/${id}`)} className="text-sm text-gray hover:text-white" data-testid="wizard-exit-btn">← Back</button>
          <p className="text-sm font-bold text-lime" data-testid="wizard-progress">Step {step + 1} of {STEPS.length}</p>
        </div>
        <div className="h-2 rounded-full bg-divider mb-4 overflow-hidden">
          <div className="h-full bg-lime transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
        <div className="flex justify-between text-xs text-gray mb-8">
          {STEPS.map((s, i) => <span key={s} className={i <= step ? 'text-lime font-bold' : ''}>{s}</span>)}
        </div>

        {step === 0 && (
          <div className="text-center">
            <Target size={28} className="text-lime mx-auto mb-3" />
            <h1 className="text-3xl font-bold text-white mb-6 flex items-center justify-center gap-2">What's the goal? <HelpTooltip text="Your goal decides which ads run, what the landing page pushes, and how we judge the numbers. There's no wrong answer — pick what you need most this month." testId="help-wizard-goal" /></h1>
            <div className="space-y-3 mb-5">
              {GOALS.map((g) => (
                <button key={g.id} onClick={() => { setGoal(g); setStep(1); }} data-testid={`goal-${g.id}`}
                  className="w-full p-4 rounded-xl border border-divider bg-card-bg text-left hover:border-lime transition-colors">
                  <p className="text-white font-bold">{g.label}</p>
                  <p className="text-sm text-gray">{g.desc}</p>
                </button>
              ))}
            </div>
            <InlineTip>Pick what you need most THIS month → the ads and landing page tune to it. Change it anytime.</InlineTip>
          </div>
        )}

        {step === 1 && (
          <div className="text-center">
            <MapPin size={28} className="text-lime mx-auto mb-3" />
            <h1 className="text-3xl font-bold text-white mb-6 flex items-center justify-center gap-2">Where do you want jobs? <HelpTooltip text="Your ads only show to people in this area — so you never pay for a click from someone you can't send a truck to. Tighter area = cheaper leads." testId="help-wizard-area" /></h1>
            <div className="flex gap-2 mb-4 justify-center">
              {[['radius', 'Radius around my shop'], ['cities', 'Specific cities']].map(([v, l]) => (
                <button key={v} onClick={() => { setAreaType(v); setAreaValue(v === 'radius' ? '15 miles' : ''); }} data-testid={`area-type-${v}`}
                  className={`px-4 py-2 rounded-full border text-sm font-bold ${areaType === v ? 'bg-lime text-black border-lime' : 'border-divider text-light-gray'}`}>{l}</button>
              ))}
            </div>
            {areaType === 'radius' ? (
              <div className="flex gap-2 justify-center mb-5">
                {['10 miles', '15 miles', '25 miles', '40 miles'].map((r) => (
                  <button key={r} onClick={() => setAreaValue(r)} data-testid={`radius-${r.split(' ')[0]}`}
                    className={`px-4 py-2 rounded-lg border text-sm font-bold ${areaValue === r ? 'bg-lime text-black border-lime' : 'border-divider text-light-gray'}`}>{r}</button>
                ))}
              </div>
            ) : (
              <input value={areaValue} onChange={(e) => setAreaValue(e.target.value)} placeholder="Atlanta, Marietta, Decatur"
                data-testid="area-cities-input"
                className="w-full bg-card-bg border border-divider rounded-xl px-4 py-3 text-white mb-5 focus:border-lime focus:outline-none" />
            )}
            <InlineTip>Tighter area → cheaper leads. Only pay for clicks where you can actually send a truck.</InlineTip>
            <button onClick={() => setStep(2)} disabled={!areaValue.trim()} data-testid="step-next-btn"
              className="mt-5 w-full py-4 rounded-xl bg-lime text-black font-black text-lg disabled:opacity-30 hover:bg-lime/90">Next →</button>
          </div>
        )}

        {step === 2 && (
          <div className="text-center">
            <DollarSign size={28} className="text-lime mx-auto mb-3" />
            <h1 className="text-3xl font-bold text-white mb-6 flex items-center justify-center gap-2">Pick your budget <HelpTooltip text="This is the MOST the ad platform can spend per day — it never goes over. The lead estimates are typical ranges for the trades; your area and season shift them a bit." testId="help-wizard-budget" /></h1>
            <div className="space-y-3 mb-5">
              {TIERS.map((t) => (
                <button key={t.id} onClick={() => { setTier(t); setStep(3); }} data-testid={`tier-${t.id}`}
                  className={`w-full p-4 rounded-xl border text-left transition-colors ${t.recommended ? 'border-lime bg-lime/5' : 'border-divider bg-card-bg hover:border-lime/50'}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-white font-bold">{t.label}{t.recommended && <span className="ml-2 text-xs text-lime font-bold uppercase">Recommended</span>}</p>
                    <p className="text-lime font-black text-xl">${t.daily}<span className="text-xs text-gray font-normal">/day</span></p>
                  </div>
                  <p className="text-sm text-gray">{t.leads}</p>
                </button>
              ))}
            </div>
            <InlineTip>Set a budget you can hold for 2 weeks → the system learns who your buyers are. Change it any day — you control the card.</InlineTip>
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="text-center">
              <ClipboardCheck size={28} className="text-lime mx-auto mb-3" />
              <h1 className="text-3xl font-bold text-white mb-2">Review your campaign</h1>
              <p className="text-light-gray mb-6">This is exactly what goes live. Check the ad, the copy, and the landing page before you launch.</p>
            </div>

            {/* ===== THE AD ===== */}
            <div className="rounded-xl border border-divider bg-card-bg p-5 mb-5 text-left" data-testid="review-ad-preview">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h2 className="text-white font-bold">Your ad</h2>
                <div className="flex items-center gap-2">
                  {adVariants.length > 1 && adVariants.map((a, i) => (
                    <button key={a.id} onClick={() => { setAdIndex(i); setEditingAd(false); }} data-testid={`ad-variant-${i + 1}`}
                      className={`px-3 py-1 rounded-full border text-xs font-bold ${i === adIndex ? 'bg-lime text-black border-lime' : 'border-divider text-light-gray hover:border-lime/50'}`}>
                      Ad {i + 1}
                    </button>
                  ))}
                  {currentAd && !editingAd && (
                    <button onClick={() => { setDraftCopy(adContent); setEditingAd(true); }} data-testid="edit-ad-copy-btn"
                      className="flex items-center gap-1 px-3 py-1 rounded-full border border-lime/50 text-lime text-xs font-bold hover:bg-lime/10">
                      <Pencil size={11} /> Edit copy
                    </button>
                  )}
                </div>
              </div>

              {!currentAd ? (
                <p className="text-gray text-sm" data-testid="no-ad-copy">No ad copy found for this campaign yet — open the campaign kit to generate it first.</p>
              ) : editingAd ? (
                <div>
                  <textarea value={draftCopy} onChange={(e) => setDraftCopy(e.target.value)} rows={9}
                    data-testid="ad-copy-textarea"
                    className="w-full bg-black border border-lime/40 rounded-xl px-4 py-3 text-sm text-white focus:border-lime focus:outline-none mb-3" />
                  <div className="flex gap-2">
                    <button onClick={() => { setCopyOverrides({ ...copyOverrides, [currentAd.id]: draftCopy }); setEditingAd(false); }}
                      data-testid="save-ad-copy-btn"
                      className="px-4 py-2 rounded-lg bg-lime text-black text-sm font-bold hover:bg-lime/90">Save copy</button>
                    <button onClick={() => setEditingAd(false)} data-testid="cancel-ad-copy-btn"
                      className="px-4 py-2 rounded-lg border border-divider text-light-gray text-sm hover:text-white">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="max-w-md mx-auto rounded-xl overflow-hidden border border-divider bg-black" data-testid="ad-mockup">
                  {/* Facebook-style ad mock */}
                  <div className="flex items-center gap-3 p-3">
                    <div className="w-10 h-10 rounded-full bg-card-bg border border-divider overflow-hidden flex items-center justify-center shrink-0">
                      {logoUrl ? <img src={logoUrl} alt="logo" className="w-full h-full object-contain" />
                        : <span className="text-lime font-black">{((company?.company_name && company.company_name !== '[Company]' ? company.company_name : 'Y'))[0]}</span>}
                    </div>
                    <div>
                      <p className="text-white text-sm font-bold leading-tight">{company?.company_name && company.company_name !== '[Company]' ? company.company_name : 'Your Company'}</p>
                      <p className="text-gray text-xs">Sponsored</p>
                    </div>
                  </div>
                  <div className="px-3 pb-3">
                    <p className="text-white text-sm font-bold mb-1 whitespace-pre-line">{adHeadline}</p>
                    <p className="text-light-gray text-sm whitespace-pre-line">{adBody}</p>
                  </div>
                  <img src={imageUrl} alt="Ad creative" className="w-full aspect-[1.91/1] object-cover" data-testid="ad-image" />
                  <div className="flex items-center justify-between p-3 bg-card-bg">
                    <p className="text-xs text-gray uppercase">{company?.city && company.city !== '[City]' ? company.city : 'Your city'}</p>
                    <span className="flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-lime text-black text-xs font-black uppercase"><Phone size={12} /> Call Now</span>
                  </div>
                </div>
              )}

              {/* Image swap */}
              {currentAd && !editingAd && /\$X+\b/i.test(adContent) && (
                <p className="mt-3 text-sm text-orange flex items-start gap-1.5" data-testid="placeholder-warning">
                  <span className="font-bold shrink-0">⚠</span>
                  <span>This ad still has a "$X" placeholder price. <Link to={`/campaign/${id}#section-numbers`} className="underline font-bold">Enter your real numbers</Link> so homeowners see a real offer — or edit the copy above.</span>
                </p>
              )}
              {currentAd && !editingAd && (
                <div className="mt-4" data-testid="ad-image-options">
                  <p className="text-xs text-gray font-bold uppercase tracking-wide mb-2">Ad image</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => setImageUrl(TRADE_AD_IMAGES[campaign?.trade] || TRADE_AD_IMAGES.HVAC)} data-testid="img-option-default"
                      className={`rounded-lg overflow-hidden border-2 ${imageUrl === (TRADE_AD_IMAGES[campaign?.trade] || TRADE_AD_IMAGES.HVAC) ? 'border-lime' : 'border-divider'}`}>
                      <img src={TRADE_AD_IMAGES[campaign?.trade] || TRADE_AD_IMAGES.HVAC} alt={`${campaign?.trade} default`} className="w-20 h-12 object-cover" />
                    </button>
                    {logoUrl && (
                      <button onClick={() => setImageUrl(logoUrl)} data-testid="img-option-logo"
                        className={`rounded-lg overflow-hidden border-2 bg-white ${imageUrl === logoUrl ? 'border-lime' : 'border-divider'}`}>
                        <img src={logoUrl} alt="Your logo" className="w-20 h-12 object-contain" />
                      </button>
                    )}
                    <div className="flex items-center gap-2">
                      <input value={customImg} onChange={(e) => setCustomImg(e.target.value)} placeholder="Paste your own image URL…"
                        data-testid="custom-image-input"
                        className="bg-black border border-divider rounded-lg px-3 py-2 text-xs text-white w-52 focus:border-lime focus:outline-none" />
                      <button onClick={() => customImg.trim() && setImageUrl(customImg.trim())} disabled={!customImg.trim()} data-testid="use-custom-image-btn"
                        className="px-3 py-2 rounded-lg border border-lime/50 text-lime text-xs font-bold disabled:opacity-30 hover:bg-lime/10">Use</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ===== THE LANDING PAGE ===== */}
            <div className="rounded-xl border border-divider bg-card-bg p-5 mb-5 text-left" data-testid="review-landing-preview">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-white font-bold">Where the ad sends people</h2>
                <a href={`/public/campaign/${id}`} target="_blank" rel="noopener noreferrer" data-testid="open-landing-page-link"
                  className="flex items-center gap-1 text-lime text-xs font-bold hover:underline">Open full page <ExternalLink size={12} /></a>
              </div>
              <div className="rounded-lg overflow-hidden border border-divider bg-black">
                <iframe src={`/public/campaign/${id}`} title="Landing page preview" className="w-full h-96 bg-white" data-testid="landing-page-iframe" />
              </div>
            </div>

            {/* ===== PLAN SUMMARY ===== */}
            <div className="rounded-xl border border-divider bg-card-bg p-5 text-left space-y-3 mb-5" data-testid="launch-review">
              <div className="flex justify-between"><span className="text-gray flex items-center gap-1.5">Goal <HelpTooltip size={13} text="What you told us you want most right now — it shaped the ads and the landing page." testId="help-review-goal" /></span><span className="text-white font-bold">{goal?.label}</span></div>
              <div className="flex justify-between"><span className="text-gray flex items-center gap-1.5">Area <HelpTooltip size={13} text="Only people in this area see your ads — you never pay for clicks you can't serve." testId="help-review-area" /></span><span className="text-white font-bold">{areaValue}</span></div>
              <div className="flex justify-between"><span className="text-gray flex items-center gap-1.5">Budget <HelpTooltip size={13} text="The daily ceiling. You can raise, lower, or stop it any day — you're in control." testId="help-review-budget" /></span><span className="text-lime font-bold">${tier?.daily}/day · {tier?.label}</span></div>
              <div className="flex justify-between"><span className="text-gray flex items-center gap-1.5">Runs in <HelpTooltip size={13} text="The campaign lives in your own Google/Meta account. You own the account, the data, and the card — nothing is held hostage." testId="help-review-account" /></span><span className="text-white font-bold">YOUR ad account</span></div>
            </div>
            <InlineTip>Nothing spends without your approval — the ads run in YOUR Google/Meta account.</InlineTip>
            {adConnected === false ? (
              <div className="mt-5 rounded-xl border border-[#ec6a1c]/60 bg-[#ec6a1c]/10 p-5 text-center" data-testid="launch-connect-gate">
                <p className="text-[#ec6a1c] font-bold mb-3">Connect your ad account to continue</p>
                <p className="text-sm text-light-gray mb-4">Campaign launch stays locked until your Google Ads or Meta account is really connected.</p>
                <Link to="/connections" data-testid="launch-connect-btn"
                  className="inline-block px-6 py-3 rounded-xl bg-[#ec6a1c] text-white font-black hover:opacity-90">Go to Connections →</Link>
              </div>
            ) : (
              <button onClick={launch} disabled={launching || adConnected !== true} data-testid="launch-btn"
                className="mt-5 w-full py-4 rounded-xl bg-lime text-black font-black text-lg hover:bg-lime/90 disabled:opacity-50">
                {launching ? 'Launching…' : adConnected === null ? 'Checking your ad account…' : 'Approve & Launch'}
              </button>
            )}
            <div className="text-center">
              <button onClick={() => setStep(2)} className="mt-3 text-sm text-gray hover:text-white" data-testid="step-back-btn">← Back</button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
